import { FolderOpen, FolderSync, RotateCw, MonitorX, Unplug } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDocumentCaptureStore, type StationState } from '../store/documentCaptureStore';

/**
 * Scan Station status pill + connection banner (86d3rrfbf). All copy is VERBATIM from the
 * `.dc.html` prototype — it is the entire self-serve explanation of what the station is doing.
 *
 * Note the prototype's key for the paused state is `resume`, not `paused`.
 */

const STATION_PILL: Record<StationState, { dot: string; title: string; pulse: boolean }> = {
  watching: { dot: 'bg-success', title: 'Watching', pulse: true },
  nofolder: { dot: 'bg-muted-foreground', title: 'Not connected', pulse: false },
  resume: { dot: 'bg-info', title: 'Paused', pulse: false },
  unsupported: { dot: 'bg-muted-foreground', title: 'Unsupported', pulse: false },
  lost: { dot: 'bg-destructive', title: 'Connection lost', pulse: false },
};

const STATION_TIP: Record<StationState, string> = {
  // 86d3yek7d — the number in this pill used to be a counter living on the browser tab, so it started
  // again at 0 on every reload. It is now counted on the server; the tooltip says so, and says which
  // window it covers, because "today" was doing a lot of unearned work in the old copy.
  watching:
    'DOP checks this folder every few seconds and captures each PDF automatically — no clicks needed. The count is scans uploaded from this station since midnight (Manila).',
  nofolder: 'Connect the scanner’s output folder to start automatic capture.',
  resume: 'Watching is paused — click Resume to continue automatic capture.',
  unsupported: 'Automatic watching needs Chrome or Edge on the scanning PC.',
  lost: 'The folder connection dropped — capture is paused until you reconnect.',
};

/**
 * Verbatim design copy. The reassurance is the whole point of the control, and it has to be
 * readable BEFORE the click — an operator who suspects switching folders loses the day's scans
 * simply never switches.
 */
const CHANGE_FOLDER_TIP =
  'Point DOP at a different folder — after a scanner swap, or when the scans move to another drive. Files already picked up stay picked up; nothing is re-read.';

interface StationStatusPillProps {
  state: StationState;
  folderName: string | null;
  /**
   * 86d3yek7d — scans uploaded from THIS station today, counted on the server from `StationId` +
   * `UploadedAt`. Null while unknown (still loading, fetch failed, or nothing has been scanned here
   * under a station id), in which case the pill simply omits the count.
   *
   * It is not the same number as the card's "scanned": that one covers every station.
   */
  scannedHereToday: number | null;
  /**
   * Opens the folder picker. Rendered only while `watching` — every other state already carries its
   * own CTA in the connection banner, and a second one here would just compete with it.
   */
  onChangeFolder?: () => void;
}

export function StationStatusPill({
  state,
  folderName,
  scannedHereToday,
  onChangeFolder,
}: StationStatusPillProps) {
  const pill = STATION_PILL[state];
  const meta =
    state === 'watching'
      ? // No count yet → no claim. A "0 scanned here today" that is really "we haven't asked yet"
        // is the same species of lie this ticket exists to remove.
        scannedHereToday === null
        ? ''
        : `· ${scannedHereToday} scanned here today`
      : state === 'resume'
        ? '· reconnect to resume'
        : state === 'unsupported'
          ? '· use Chrome/Edge'
          : state === 'lost'
            ? '· paused'
            : '';

  return (
    // The pill's chrome moved to this wrapper so `Change` can be a SIBLING of the described status
    // text. Nesting a button inside the TooltipTrigger would be nested interactive content — invalid
    // HTML, and the trigger would own the click target.
    //
    // The left/vertical padding stays ON the trigger (`py-1.5 pl-3`) rather than moving out here, so
    // the status tooltip keeps the whole pill as its hover target instead of shrinking to the text.
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card pr-3 text-sm">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help items-center gap-2 py-1.5 pl-3">
            <span
              className={cn(
                'h-2 w-2 flex-shrink-0 rounded-full',
                pill.dot,
                pill.pulse && 'motion-safe:animate-pulse'
              )}
              aria-hidden
            />
            <span className="font-medium text-foreground">{pill.title}</span>
            {folderName && <span className="font-mono text-xs text-muted-foreground">{folderName}</span>}
            {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{STATION_TIP[state]}</TooltipContent>
      </Tooltip>

      {state === 'watching' && onChangeFolder && (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Icon PLUS a visible label, so this is not an icon-only control: its accessible name
                is "Change" and the tooltip rides along as the description (Radix wires
                aria-describedby). An aria-label carrying the tooltip paragraph would REPLACE that
                name — an a11y regression, not the gate's icon-only remedy.
                `[&_svg]:size-3` is the 12×12 icon the design asks for; the Button base forces
                size-4, and tailwind-merge resolves the conflict in favour of this one. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onChangeFolder}
              className="h-6 flex-shrink-0 gap-1.5 bg-card px-2 text-muted-foreground [&_svg]:size-3"
            >
              <FolderSync aria-hidden />
              Change
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{CHANGE_FOLDER_TIP}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- queue depth

/**
 * A batch has to be this big before the line appears. One or two scans at a time is the ordinary
 * case and needs no commentary — a line that flashes up on every single scan is noise, and it would
 * also make an operator wonder what changed. Above this, the operator has pasted in a batch and the
 * number is the only thing on the page that can tell them how it is going.
 *
 * Also why the copy never needs a singular: it only ever renders three or more.
 */
const QUEUE_LINE_THRESHOLD = 3;

const QUEUE_LINE_TIP =
  'Big batches are taken a few at a time, and a PDF is only uploaded once it has stopped growing — that is what keeps a half-written scan out of the archive. Keep dropping files in; the folder is re-checked every few seconds.';

/**
 * Queue depth beside the Watching pill (lane-g). Subscribes to the store ITSELF rather than taking
 * props, so a number that changes on every finished upload re-renders this line alone instead of
 * the capture page — which renders the live stream and the whole archive table below it.
 *
 * Value selectors only, and `remaining` is derived from the subscribed value (CLAUDE.md 86d3d1z40).
 */
export function ScanQueueLine() {
  const state = useDocumentCaptureStore((s) => s.stationState);
  const queue = useDocumentCaptureStore((s) => s.stationQueue);

  if (state !== 'watching' || !queue) return null;
  const remaining = queue.pending + queue.waiting + queue.uploading;
  if (remaining < QUEUE_LINE_THRESHOLD) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className="cursor-help text-xs text-muted-foreground">
          Working through {remaining.toLocaleString()} scans —{' '}
          {queue.uploading > 0
            ? `${queue.uploading} uploading now.`
            : 'checking each one has finished writing.'}
        </p>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{QUEUE_LINE_TIP}</TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------- connection banner

const BANNERS: Record<
  Exclude<StationState, 'watching'>,
  { tone: 'info' | 'muted' | 'destructive'; icon: typeof FolderOpen; title: string; body: string; cta: string }
> = {
  nofolder: {
    tone: 'info',
    icon: FolderOpen,
    title: 'Connect the scanner’s output folder',
    body: 'Point DOP at the folder your scanner drops PDFs into. One-time setup; files move to an uploaded\\ subfolder after capture.',
    cta: 'Connect folder',
  },
  resume: {
    tone: 'info',
    icon: RotateCw,
    title: 'Reconnect to the watched folder?',
    body: 'Your browser needs one click to resume watching this folder.',
    cta: 'Resume',
  },
  unsupported: {
    tone: 'muted',
    icon: MonitorX,
    title: 'Scan Station needs Chrome or Edge',
    body: 'Automatic folder watching is Chromium-only. Open on the scanning PC in Chrome/Edge — or drop files manually below.',
    cta: '',
  },
  lost: {
    tone: 'destructive',
    icon: Unplug,
    title: 'Folder connection lost',
    body: 'The drive was removed or access revoked. Capture is paused — drop today’s scans manually to keep working.',
    cta: 'Reconnect',
  },
};

const BANNER_CLASSES: Record<'info' | 'muted' | 'destructive', string> = {
  info: 'border-info/25 bg-info/8 text-info',
  muted: 'border-border bg-muted text-muted-foreground',
  destructive: 'border-destructive/25 bg-destructive/8 text-destructive',
};

interface ConnectionBannerProps {
  state: StationState;
  folderName: string | null;
  onAction: () => void;
}

export function ConnectionBanner({ state, folderName, onAction }: ConnectionBannerProps) {
  if (state === 'watching') return null;
  const banner = BANNERS[state];
  const Icon = banner.icon;

  return (
    <div
      className={cn('mb-4 flex items-start gap-3 rounded-xl border p-4', BANNER_CLASSES[banner.tone])}
      // A lost folder means capture has silently stopped — announce it rather than let the
      // operator discover it by noticing nothing arriving.
      role={state === 'lost' ? 'alert' : undefined}
    >
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">
          {state === 'resume' && folderName ? `Reconnect to ${folderName}?` : banner.title}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">{banner.body}</p>
      </div>
      {banner.cta && (
        <Button size="sm" variant="outline" onClick={onAction} className="flex-shrink-0">
          {banner.cta}
        </Button>
      )}
    </div>
  );
}
